import React from 'react';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { getURLTickers } from '../ProfileScreen/util';
import styles from './styles.css';
import {
  getChecklistSSNText,
  getCorrespondingBRKAccLinks,
  getStepsSectionSecondHeader,
  getStepsSectionThirdHeader,
  getTaxableChecklistHeaderText,
  getTaxableChecklistSubHeaderText,
  isNonUGMATaxable,
  navigateToLoginFromChecklist
} from './utils';
import {
  accountTypes,
  formatAgreementTypeForDisplay,
  redirectToAccountFinder
} from '../../flows/shared/utils';

const StepsSection = ({ accountType }) => {
  return (
    <>
      <section>
        <div className="checklist-section-title">
          Opening an account takes 5-10 minutes:
        </div>
        <dl>
          <ol>
            <li aria-describedby="basic-investment-info">
              <dt data-name="1">Provide basic investment details</dt>
              <dd id="basic-investment-info">
                This helps us tailor your investment options.
              </dd>
            </li>
            <li aria-describedby="investment-info">
              <dt data-name="2">{getStepsSectionSecondHeader(accountType)}</dt>
              <dd id="investment-info">
                <a
                  href="https://www.troweprice.com/personal-investing/tools/fund-research"
                  target="_blank"
                  rel="noopener noreferrer">
                  Research Mutual Funds
                </a>{' '}
                or{' '}
                <a
                  href={getCorrespondingBRKAccLinks(accountType)}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="switch-to-brk-link">
                  switch to a Brokerage{' '}
                  {formatAgreementTypeForDisplay(accountType)} account
                </a>{' '}
                to invest in ETFs, stocks, and other securities.
              </dd>
            </li>
            <li aria-describedby="personal-details-info">
              <dt data-name="3">{getStepsSectionThirdHeader(accountType)}</dt>
              <dd id="personal-details-info">
                We&apos;ll use these to secure and verify the account.
              </dd>
            </li>
          </ol>
        </dl>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

export const ChecklistSection = ({ accountType }) => {
  const accountMinimum = isNonUGMATaxable(accountType) ? '$2,500' : '$1,000';

  return (
    <>
      <section>
        <div className="checklist-section-title">You will need:</div>
        <ul data-name="Checklist_RequirementsList">
          <li data-name="Checklist_SocialSecurityText">
            {getChecklistSSNText(accountType)}
          </li>
          <li data-name="Checklist_CitizenshipText">
            Permanent U.S. residence
          </li>
          <li data-name="Checklist_MinimumInvestmentText">
            {accountMinimum} minimum investment
          </li>
        </ul>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const TaxableChecklistScreen = ({ appState, next }) => {
  const {
    store: { authenticated, accountType }
  } = appState;

  const taxableAccountHeader = getTaxableChecklistHeaderText(accountType);

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  return (
    <div className="checklist-screen">
      <Heading text={`Open ${taxableAccountHeader} account`} />
      <SubHeading text={getTaxableChecklistSubHeaderText(accountType)}>
        {accountType === accountTypes.UGMAUTMA || (
          <a
            href="https://www.troweprice.com/personal-investing/accounts/general-investing/index.html#individual"
            target="_blank"
            rel="noopener noreferrer">
            Learn about other investment options to fit your needs.
          </a>
        )}
      </SubHeading>

      <div className="qv-row">
        <div className="qv-col col-left">
          <StepsSection accountType={accountType} />
        </div>

        <div className="qv-col col-right">
          <ChecklistSection accountType={accountType} />
        </div>
      </div>

      <div className="button-container">
        {authenticated ? (
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="checklistContinueExisting"
            onClick={next(multiBuyTickers)}
            label="Continue Opening an Account"
            data-name="taxableChecklistContinue"
          />
        ) : (
          <div className="login-buttons">
            <MFBeaconButton
              variant="primary"
              size="normal"
              id="checklistLogin"
              onClick={() => navigateToLoginFromChecklist('/profile')}
              label="Already a Client? Log In to Continue"
              data-name="taxableChecklistLogin"
            />
            <MFBeaconButton
              variant="secondary"
              size="normal"
              id="checklistContinueNew"
              onClick={next(multiBuyTickers)}
              label="New to T. Rowe Price? Start Now"
              data-name="taxableChecklistNewStart"
            />
          </div>
        )}
        <MFBeaconButton
          variant="tertiary"
          size="normal"
          id="checklistBack"
          onClick={redirectToAccountFinder}
          label="Back"
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default TaxableChecklistScreen;
